import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus, faSync } from "@fortawesome/free-solid-svg-icons"
import { generalStyles as gs } from "../../styles/styles"
import { COLORS } from "../../../utilities/Constants"
import CustomModal from "../../basics/interface/CustomModal"
import {
	EMAIL_DESTINATAIRE_CHOICES,
	EMAIL_DESTINATAIRE_CLIENT_PERSONNE,
	EMAIL_DESTINATAIRE_CLIENT_SOCIETE,
	EMAIL_DESTINATAIRE_ASSURANCE,
	EMAIL_DESTINATAIRE_EXPERT,
	EMAIL_DESTINATAIRE_SOUS_TRAITANT,
	EMAIL_DESTINATAIRE_LIBRE,
	EMAIL_DESTINATAIRE_CONDUCTEUR,
	EMAIL_STATUS_ERROR,
	EMAIL_STATUS_SENT,
} from "../../../utilities/Addons"
import {
	EMAIL_DOMAINE_ATELIER,
	EMAIL_DOMAINE_DEPANNAGE,
	EMAIL_DOMAINE_LOCATION,
	deleteEmailTemplate,
	fetchDernierTest,
	fetchEmailPreview,
	fetchEmailTemplates,
	saveEmailTemplate,
	sendEmailTest,
} from "../../../store/actions/emailActions"

// Onglet « Modèles d'e-mail » (/configuration, boss — GE-25).
//
// Liste à gauche, édition à droite avec la palette de variables et l'aperçu
// rendu PAR LE SERVEUR : c'est le même chemin de rendu que l'envoi, donc ce
// qu'on voit ici est ce que le client recevra. Textarea HTML et pas de WYSIWYG
// (dépendance lourde, mal supportée par CRA 3, et nos textes font 4 lignes).

const DOMAINES = [
	{ key: EMAIL_DOMAINE_ATELIER, label: "Atelier" },
	{ key: EMAIL_DOMAINE_DEPANNAGE, label: "Dépannage" },
	{ key: EMAIL_DOMAINE_LOCATION, label: "Location" },
]

const DESTINATAIRES_PAR_DOMAINE = {
	[EMAIL_DOMAINE_ATELIER]: [
		EMAIL_DESTINATAIRE_CLIENT_SOCIETE,
		EMAIL_DESTINATAIRE_CLIENT_PERSONNE,
		EMAIL_DESTINATAIRE_ASSURANCE,
		EMAIL_DESTINATAIRE_EXPERT,
		EMAIL_DESTINATAIRE_SOUS_TRAITANT,
		EMAIL_DESTINATAIRE_LIBRE,
	],
	[EMAIL_DOMAINE_DEPANNAGE]: [
		EMAIL_DESTINATAIRE_CLIENT_SOCIETE,
		EMAIL_DESTINATAIRE_CLIENT_PERSONNE,
		EMAIL_DESTINATAIRE_ASSURANCE,
		EMAIL_DESTINATAIRE_EXPERT,
		EMAIL_DESTINATAIRE_SOUS_TRAITANT,
		EMAIL_DESTINATAIRE_LIBRE,
	],
	[EMAIL_DOMAINE_LOCATION]: [
		EMAIL_DESTINATAIRE_CLIENT_SOCIETE,
		EMAIL_DESTINATAIRE_CLIENT_PERSONNE,
		EMAIL_DESTINATAIRE_ASSURANCE,
		EMAIL_DESTINATAIRE_CONDUCTEUR,
		EMAIL_DESTINATAIRE_LIBRE,
	],
}

const vide = (domaine) => ({
	id: null,
	domaine,
	destinataire_type: EMAIL_DESTINATAIRE_CLIENT_PERSONNE,
	code: "",
	label: "",
	sujet: "",
	titre: "",
	salutation: "",
	corps_html: "",
	actif: true,
	defaut: false,
})

// Marge laissée sous l'éditeur : le padding bas de gs.page vaut 32 et se
// trouve SOUS le bloc — descendre plus bas que ça fait déborder la page.
const MARGE_BAS = 36
// Plancher : sous cette hauteur on préfère faire scroller la page plutôt que
// d'écraser l'aperçu.
const HAUTEUR_MINIMALE = 420

const styles = {
	// La hauteur est MESURÉE au runtime (cf. useLayoutEffect) et non déduite
	// d'un offset en dur : la barre d'onglets passe à deux lignes selon la
	// largeur, un offset deviné se trompait donc d'une ligne entière.
	corps: {
		display: "flex",
		gap: 18,
		alignItems: "stretch",
		minHeight: HAUTEUR_MINIMALE,
	},
	liste: { flex: "0 0 260px", minWidth: 0, overflowY: "auto" },
	edition: {
		flex: "1 1 0",
		minWidth: 0,
		display: "flex",
		gap: 18,
		// minHeight:0 obligatoire : sans lui, un enfant en overflow refuse de
		// rétrécir sous sa hauteur de contenu et déborde du flex.
		minHeight: 0,
	},
	// Les champs occupent leur PROPRE colonne et scrollent : c'est ce qui rend
	// à l'aperçu toute la hauteur (empilés, ils lui prenaient la moitié).
	champs: {
		flex: "1 1 0",
		minWidth: 0,
		overflowY: "auto",
		paddingRight: 6,
	},
	colonneApercu: {
		flex: "1 1 0",
		minWidth: 0,
		display: "flex",
		flexDirection: "column",
		minHeight: 0,
	},
	actions: {
		display: "flex",
		alignItems: "flex-start",
		justifyContent: "flex-end",
		gap: 10,
		marginTop: 14,
		flexShrink: 0,
	},
	item: (actif) => ({
		padding: "9px 11px",
		borderRadius: 9,
		border: `1px solid ${actif ? COLORS.accent : COLORS.border}`,
		backgroundColor: actif ? COLORS.accentLight : COLORS.surface,
		cursor: "pointer",
		marginBottom: 6,
		fontSize: 13,
	}),
	itemMeta: { fontSize: 11, color: COLORS.textLight, marginTop: 2 },
	champ: { marginBottom: 10 },
	pastille: {
		display: "inline-block",
		padding: "2px 8px",
		margin: "0 5px 5px 0",
		borderRadius: 99,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.bg,
		fontSize: 11,
		cursor: "pointer",
		fontFamily: "monospace",
	},
	erreur: {
		padding: "9px 11px",
		borderRadius: 9,
		border: `1px solid ${COLORS.dangerBorder}`,
		backgroundColor: COLORS.dangerLight,
		color: COLORS.danger,
		fontSize: 12,
		marginBottom: 10,
	},
	apercu: {
		border: `1px solid ${COLORS.border}`,
		borderRadius: 10,
		overflow: "hidden",
		// basis 0 + grow : l'aperçu prend TOUTE la hauteur de sa colonne.
		flex: "1 1 0",
		minHeight: 0,
		marginTop: 10,
	},
}

const EmailTemplatesV2 = () => {
	const dispatch = useDispatch()
	const templates = useSelector((s) => s.emailInfo.templates)
	const masterEntite = useSelector((s) => s.masterReducer.masterEntite)
	const emailTest = (masterEntite?.extra_data || {}).email_test || ""
	const [domaine, setDomaine] = useState(EMAIL_DOMAINE_ATELIER)
	const [testPhase, setTestPhase] = useState("idle")
	const [dernierTest, setDernierTest] = useState(null)
	const [edition, setEdition] = useState(null)
	const [erreur, setErreur] = useState("")
	const [apercu, setApercu] = useState(null)
	const [confirmation, setConfirmation] = useState(null)
	const [enCours, setEnCours] = useState(false)
	const corpsRef = useRef(null)
	const [hauteur, setHauteur] = useState(null)

	// On MESURE le haut du bloc puis on descend jusqu'au bas de la fenêtre :
	// un offset en dur se trompait dès que la barre d'onglets passait à deux
	// lignes (la largeur de la fenêtre change le nombre de lignes).
	const mesurer = useCallback(() => {
		if (corpsRef.current === null) return
		const haut = corpsRef.current.getBoundingClientRect().top
		setHauteur(
			Math.max(HAUTEUR_MINIMALE, window.innerHeight - haut - MARGE_BAS),
		)
	}, [])

	useLayoutEffect(() => {
		mesurer()
		window.addEventListener("resize", mesurer)
		return () => window.removeEventListener("resize", mesurer)
	}, [mesurer])

	useEffect(() => {
		dispatch(fetchEmailTemplates())
		dispatch(fetchDernierTest()).then((d) => setDernierTest(d))
	}, [dispatch])

	const duDomaine = useMemo(
		() => templates.filter((t) => t.domaine === domaine),
		[templates, domaine],
	)

	// Aperçu redemandé au serveur à chaque frappe (léger débounce) : le rendu
	// est la seule preuve de ce qui partira.
	useEffect(() => {
		if (edition === null) return undefined
		const t = setTimeout(() => {
			dispatch(
				fetchEmailPreview({
					domaine: edition.domaine,
					sujet: edition.sujet,
					titre: edition.titre,
					salutation: edition.salutation,
					corps_html: edition.corps_html,
				}),
			).then((json) => {
				if (json) setApercu(json)
			})
		}, 350)
		return () => clearTimeout(t)
	}, [dispatch, edition])

	const maj = (cle, valeur) => setEdition((e) => ({ ...e, [cle]: valeur }))

	const enregistrer = () => {
		setEnCours(true)
		setErreur("")
		dispatch(saveEmailTemplate(edition)).then((json) => {
			setEnCours(false)
			setConfirmation(null)
			// Le serveur refuse une variable inconnue : le message porte le nom
			// fautif, on l'affiche ici plutôt qu'en toast.
			if (!json || json.message) {
				setErreur(
					json && json.message
						? json.message
						: "Le modèle n'a pas pu être enregistré.",
				)
				return
			}
			setEdition(json)
			dispatch(fetchEmailTemplates())
		})
	}

	const supprimer = () => {
		dispatch(deleteEmailTemplate(edition.id)).then(() => {
			setConfirmation(null)
			setEdition(null)
		})
	}

	return (
		<div>
			<div style={gs.sectionTabs}>
				{DOMAINES.map((d) => (
					<button
						key={`dom_${d.key}`}
						type="button"
						style={gs.sectionTab(domaine === d.key)}
						onClick={() => {
							setDomaine(d.key)
							setEdition(null)
						}}
					>
						{d.label}
					</button>
				))}
			</div>

			<div
				ref={corpsRef}
				style={
					hauteur === null
						? styles.corps
						: { ...styles.corps, height: hauteur }
				}
			>
				<div style={styles.liste}>
					{duDomaine.map((t) => (
						<div
							key={`tpl_${t.id}`}
							style={styles.item(edition !== null && edition.id === t.id)}
							onClick={() => {
								setEdition({ ...t })
								setErreur("")
							}}
						>
							<div style={{ fontWeight: 600 }}>{t.label}</div>
							<div style={styles.itemMeta}>
								{t.destinataire_label}
								{t.defaut ? " · défaut" : ""}
								{t.actif ? "" : " · inactif"}
							</div>
						</div>
					))}
					<button
						type="button"
						style={{ ...gs.btnSecondary, width: "100%", marginTop: 6 }}
						onClick={() => {
							setEdition(vide(domaine))
							setErreur("")
						}}
					>
						<FontAwesomeIcon icon={faPlus} /> Nouveau modèle
					</button>
				</div>

				<div style={styles.edition}>
					{edition === null ? (
						<div style={{ color: COLORS.textMuted, fontSize: 13 }}>
							Sélectionne un modèle, ou crées-en un.
						</div>
					) : (
						<>
							<div style={styles.champs}>
								{erreur !== "" && (
									<div style={styles.erreur}>{erreur}</div>
								)}
								<div style={styles.champ}>
									<label style={gs.label}>Nom</label>
									<input
										style={{ ...gs.input, width: "100%" }}
										value={edition.label}
										onChange={(e) => maj("label", e.target.value)}
									/>
								</div>
								<div style={styles.champ}>
									<label style={gs.label}>Code (technique, unique)</label>
									<input
										style={{ ...gs.input, width: "100%" }}
										value={edition.code}
										onChange={(e) => maj("code", e.target.value)}
									/>
								</div>
								<div style={styles.champ}>
									<label style={gs.label}>Destinataire</label>
									<select
										style={{ ...gs.select, width: "100%" }}
										value={edition.destinataire_type}
										onChange={(e) =>
											maj("destinataire_type", parseInt(e.target.value))
										}
									>
										{EMAIL_DESTINATAIRE_CHOICES
											.filter(([v]) => {
												const autorise = DESTINATAIRES_PAR_DOMAINE[edition.domaine]
												return !autorise || autorise.includes(v)
											})
											.map(([valeur, label]) => (
											<option key={`dst_${valeur}`} value={valeur}>
												{label}
											</option>
										))}
									</select>
								</div>

								{apercu !== null && apercu.palette && (() => {
									const groupes = {}
									apercu.palette.forEach((v) => {
										const g = v.groupe || "Autre"
										if (!groupes[g]) groupes[g] = []
										groupes[g].push(v)
									})
									return (
										<div style={styles.champ}>
											<label style={gs.label}>Variables disponibles</label>
											{Object.entries(groupes).map(([groupe, vars]) => (
												<div key={groupe} style={{ marginBottom: 8 }}>
													<div style={{
														fontSize: 10,
														fontWeight: 600,
														color: COLORS.textLight,
														letterSpacing: "0.06em",
														textTransform: "uppercase",
														marginBottom: 4,
													}}>
														{groupe}
													</div>
													<div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
														{vars.map((v) => (
															<span
																key={`var_${v.code}`}
																style={styles.pastille}
																title={`${v.label}${v.exemple ? ` — ${v.exemple}` : ""}`}
																onClick={() =>
																	maj(
																		"corps_html",
																		`${edition.corps_html}{{ ${v.code} }}`,
																	)
																}
															>
																{`{{ ${v.code} }}`}
															</span>
														))}
													</div>
												</div>
											))}
										</div>
									)
								})()}

								<div style={styles.champ}>
									<label style={gs.label}>Sujet</label>
									<input
										style={{ ...gs.input, width: "100%" }}
										value={edition.sujet}
										onChange={(e) => maj("sujet", e.target.value)}
									/>
								</div>
								<div style={styles.champ}>
									<label style={gs.label}>Titre (bandeau)</label>
									<input
										style={{ ...gs.input, width: "100%" }}
										value={edition.titre}
										onChange={(e) => maj("titre", e.target.value)}
									/>
								</div>
								<div style={styles.champ}>
									<label style={gs.label}>Salutation</label>
									<input
										style={{ ...gs.input, width: "100%" }}
										value={edition.salutation}
										onChange={(e) => maj("salutation", e.target.value)}
									/>
								</div>
								<div style={styles.champ}>
									<label style={gs.label}>Message (HTML)</label>
									<textarea
										style={{
											...gs.input,
											width: "100%",
											minHeight: 130,
											resize: "vertical",
											fontFamily: "monospace",
										}}
										value={edition.corps_html}
										onChange={(e) => maj("corps_html", e.target.value)}
									/>
								</div>
								<div style={{ ...styles.champ, display: "flex", gap: 18 }}>
									<label style={{ fontSize: 13 }}>
										<input
											type="checkbox"
											checked={edition.actif}
											onChange={(e) => maj("actif", e.target.checked)}
										/>{" "}
										Actif
									</label>
									<label style={{ fontSize: 13 }}>
										<input
											type="checkbox"
											checked={edition.defaut}
											onChange={(e) => maj("defaut", e.target.checked)}
										/>{" "}
										Modèle par défaut de ce destinataire
									</label>
								</div>
							</div>

							<div style={styles.colonneApercu}>
								<label style={{ ...gs.label, flexShrink: 0 }}>
									Aperçu (valeurs d'exemple)
								</label>
								<div style={styles.apercu}>
									<iframe
										title="Aperçu du modèle"
										srcDoc={apercu !== null ? apercu.html : ""}
										style={{
											width: "100%",
											height: "100%",
											border: "none",
										}}
									/>
								</div>

								<div style={styles.actions}>
									{edition.id !== null && (
										<button
											type="button"
											style={gs.btnDanger}
											onClick={() => setConfirmation("suppression")}
										>
											Supprimer
										</button>
									)}
									<div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
										<button
											type="button"
											style={{
												...gs.btnSecondary,
												...((!emailTest || testPhase !== "idle") && {
													opacity: 0.4,
													cursor: "not-allowed",
												}),
											}}
											disabled={!emailTest || testPhase !== "idle"}
											title={
												emailTest
													? `Envoyer à ${emailTest}`
													: "Configurez un e-mail de test dans Identité"
											}
											onClick={() => {
												setTestPhase("sending")
												dispatch(
													sendEmailTest({
														domaine,
														destinataire_type:
															edition.destinataire_type,
														sujet: edition.sujet,
														titre: edition.titre,
														salutation: edition.salutation,
														corps_html: edition.corps_html,
														recipient: emailTest,
													}),
												)
													.then((r) => {
														if (r) {
															setTestPhase("done")
															dispatch(fetchDernierTest()).then(
																(d) => setDernierTest(d),
															)
															setTimeout(
																() => setTestPhase("idle"),
																2000,
															)
														} else {
															setTestPhase("idle")
														}
													})
													.catch(() => setTestPhase("idle"))
											}}
										>
											{testPhase === "sending"
												? "Envoi…"
												: testPhase === "done"
													? "Mis en file ✓"
													: "Envoyer un test"}
										</button>
										{dernierTest && (
											<span style={{ fontSize: 10, color: COLORS.textLight }}>
												{dernierTest.status === EMAIL_STATUS_SENT
													? `Envoyé le ${dernierTest.sent_at}`
													: dernierTest.status === EMAIL_STATUS_ERROR
														? `Échec : ${dernierTest.error_message || "erreur"}`
														: "En attente"}
											</span>
										)}
									</div>
									<button
										type="button"
										style={gs.btnPrimary}
										onClick={() => setConfirmation("enregistrement")}
									>
										Enregistrer
									</button>
								</div>
							</div>
						</>
					)}
				</div>
			</div>

			{/* Convention V2 : un bouton de sauvegarde/suppression HORS modal
			    demande confirmation dans un modal. */}
			{confirmation !== null && (
				<CustomModal
					title={
						confirmation === "suppression"
							? "Supprimer le modèle ?"
							: "Enregistrer les modifications ?"
					}
					onClose={() => setConfirmation(null)}
					buttons={[
						{
							title: "Annuler",
							styleName: "secondary",
							onClick: () => setConfirmation(null),
						},
						{
							title: enCours ? "..." : "Confirmer",
							styleName:
								confirmation === "suppression" ? "danger" : "primary",
							onClick:
								confirmation === "suppression" ? supprimer : enregistrer,
						},
					]}
				>
					<div style={{ fontSize: 13, color: COLORS.textMuted }}>
						{confirmation === "suppression"
							? "Les e-mails déjà envoyés avec ce modèle gardent leur contenu : il est figé sur chaque envoi."
							: "Le modèle sera utilisé pour les prochains e-mails de ce destinataire."}
						{enCours && (
							<FontAwesomeIcon icon={faSync} spin style={{ marginLeft: 8 }} />
						)}
					</div>
				</CustomModal>
			)}
		</div>
	)
}

export default EmailTemplatesV2
